import React from "react";

import { isEmpty } from "lodash/fp";

import { Divider } from "@mui/material";
import Typography from "@mui/material/Typography";

import {
  SCORE_LABELS,
  SCORE_METRICS,
  GPT_ENTITY_CONFIG,
  GPT_ENTITY_KEYS,
} from "@app/analytics/Home/components/constants";
import type { MeetingReportFeedItem } from "@app/analytics/Home/types";
import { getFormattedMeetingReportDate } from "@app/analytics/Home/utils/parsingUtils";
import ROUTES from "@app/common/routes";
import {
  pluralizePhrase,
  splitParagraphs,
  truncateAlongWordBoundary,
} from "@app/common/utils";

import Stack, { Row } from "@rui/Stack";

import BaseHomeFeedItemLayout from "./BaseHomeFeedItemLayout";
import { Content, Footer } from "./styles";
import SharedAlert from "../SharedAlert";
import { ActionButton, ScoreBox } from "../styles";
import * as FeedComponentUtils from "../utils";

const SUMMARY_CHARACTER_LIMIT = 150;

const getTruncatedSummary = (summary: string) => {
  const paragraphs = splitParagraphs(summary);
  return truncateAlongWordBoundary(paragraphs[0], SUMMARY_CHARACTER_LIMIT);
};

interface MeetingReportItemProps extends MeetingReportFeedItem {
  onClick: (id: string) => void;
}

const MeetingReportItem = ({ onClick, ...item }: MeetingReportItemProps) => {
  const { data, session } = item;

  const hasGptEntities = FeedComponentUtils.itemHasGptEntities(item);
  const hasScores = FeedComponentUtils.itemHasAffectiveMetrics(item);

  const hasContent =
    hasGptEntities || hasScores || Boolean(data.metrics?.summary);

  const isNotetakerReport = data.reportType === "notetaker";

  return (
    <BaseHomeFeedItemLayout
      {...item}
      onClick={onClick}
      alert={
        isEmpty(data.sharing) ? undefined : <SharedAlert {...data.sharing} />
      }
    >
      <Stack spacing={1}>
        {hasContent && (
          <Content spacing={1.5} type={item.type}>
            {isNotetakerReport && (
              <Typography variant="bodySecondaryMed">
                {getFormattedMeetingReportDate(session.startTime)}
              </Typography>
            )}
            {data.metrics.summary ? (
              <Typography variant="bodySecondaryReg" color="shades.neutral.700">
                {getTruncatedSummary(data.metrics.summary)}
              </Typography>
            ) : isNotetakerReport ? null : (
              <Typography
                variant="bodySecondaryReg"
                color="shades.neutral.700"
                fontStyle="italic"
              >
                No summary generated
              </Typography>
            )}
            {hasGptEntities && (
              <Stack spacing={0.75} color="shades.neutral.600">
                {GPT_ENTITY_KEYS.map((metric) => {
                  const value = data.metrics[metric] as number;
                  if (!value) return null;

                  const { icon, label } = GPT_ENTITY_CONFIG[metric]!;
                  return (
                    <Row variant="left" spacing={1} key={metric}>
                      <icon.Icon sx={{ color: icon.color, fontSize: "16px" }} />
                      <Typography variant="bodySecondaryReg">
                        {pluralizePhrase(value, label)}
                      </Typography>
                    </Row>
                  );
                })}
              </Stack>
            )}
            {hasScores && (
              <Stack spacing={1}>
                <Divider />
                <Row variant="left" spacing={2} flexWrap="wrap">
                  {SCORE_METRICS.map((metric) => {
                    const score = data.metrics[metric] as number;
                    if (!score) return null;

                    return (
                      <ScoreBox
                        key={metric}
                        direction="row"
                        variant="left"
                        score={score}
                        label={SCORE_LABELS[metric]!}
                      />
                    );
                  })}
                </Row>
                <Divider />
              </Stack>
            )}
          </Content>
        )}
        <Footer>
          <ActionButton href={ROUTES.meetingReportById(session.id)}>
            View Report
          </ActionButton>
        </Footer>
      </Stack>
    </BaseHomeFeedItemLayout>
  );
};

export default MeetingReportItem;
